import React from 'react';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalMessages } from 'src/registry/utils/messages';
import { useOnError } from 'src/utils/errors';
import { ICON_MAP } from 'src/utils/constants';
import { GeneralASError } from 'src/registry/types/axios';
import { cn } from 'src/utils';
import { useParams } from 'react-router-dom';
import { ServicePlanItemPricingPolicy } from 'src/features/servicePlanPricingPolicies/utils/types';
import {
    servicePlanPricingPolicyFormSchema,
    ServicePlanPricingPolicyFormSchemaValues,
} from 'src/features/servicePlanPricingPolicies/utils/formSchema';
import { useGetServicePlanPricingPolicy } from 'src/features/servicePlanPricingPolicies/api/useGetServicePlanPricingPolicy';
import ServicePlanPricingPolicyForm from 'src/features/servicePlanPricingPolicies/components/forms/ServicePlanPricingPolicyForm';
import { getDirtyValues } from 'src/utils/getDirtyValues';

interface ServicePlanPricingPolicyFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedServicePlanPricingPolicy?: ServicePlanItemPricingPolicy;
    children?: React.ReactNode;
    isOpen?: boolean;
    isPending: boolean;
    icon: IconProp;
    title: string;
    subtitle: string;
    mutate: (
        data: ServicePlanPricingPolicyFormSchemaValues,
        onSuccess: () => void,
        onError: (error: GeneralASError) => void,
    ) => void;
}

function ServicePlanBillingPolicyFormModal<TData>({
    selectedServicePlanPricingPolicy,
    isPending,
    children,
    className,
    icon,
    title,
    subtitle,
    mutate,
}: ServicePlanPricingPolicyFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { servicePlanItemId = '' } = useParams();

    const { data: servicePlanPricingPolicy } = useGetServicePlanPricingPolicy({
        servicePlanItemId: servicePlanItemId ?? '',
        pricingPolicyId: selectedServicePlanPricingPolicy?.id ?? '',
        config: { enabled: !!selectedServicePlanPricingPolicy?.id && isModalOpen },
    });
    const onError = useOnError();

    const defaultValues = React.useMemo(() => {
        return {
            valid_from: servicePlanPricingPolicy?.valid_from ?? undefined,
            payment_type: servicePlanPricingPolicy?.payment_type ?? undefined,
            amount: servicePlanPricingPolicy?.amount ?? undefined,
            valid_to: servicePlanPricingPolicy?.valid_to ?? undefined,
            tax_reason: selectedServicePlanPricingPolicy?.tax_reason.id ?? undefined,
            pricing_model: servicePlanPricingPolicy?.pricing_model ?? undefined,
        };
    }, [servicePlanPricingPolicy]);

    const form = useForm<ServicePlanPricingPolicyFormSchemaValues>({
        resolver: zodResolver(servicePlanPricingPolicyFormSchema),
        defaultValues,
    });

    React.useEffect(() => {
        form.reset(defaultValues);
    }, [form, isModalOpen, defaultValues]);

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) form.reset();
        setIsModalOpen(isOpen);
    };

    const onSubmit = React.useCallback(
        (values: ServicePlanPricingPolicyFormSchemaValues) => {
            const dirtyFields = getDirtyValues(form.formState.dirtyFields, values);
            mutate(
                { ...dirtyFields } as ServicePlanPricingPolicyFormSchemaValues,
                () => setIsModalOpen(false),
                (error) => onError(error, form.setError),
            );
        },

        [form.formState.dirtyFields, form.setError, mutate, onError],
    );

    return (
        <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
            {children ? (
                <DialogTrigger asChild>{children}</DialogTrigger>
            ) : (
                <DialogTrigger asChild>
                    <Button className={cn('ml-auto', className)}>
                        <FontAwesomeIcon icon={ICON_MAP.ADD} />
                        {title}
                    </Button>
                </DialogTrigger>
            )}
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={icon} />
                        <div>
                            <DialogTitle>{title}</DialogTitle>
                            <DialogDescription>{subtitle}</DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <ServicePlanPricingPolicyForm form={form} onSubmit={onSubmit} />
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage {...generalMessages.cancel} />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="default"
                        disabled={isPending || (selectedServicePlanPricingPolicy && !form.formState.isDirty)}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPending && <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />}
                        {title}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default ServicePlanBillingPolicyFormModal;
